import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Menu,
  MenuItem,
  IconButton
} from "@mui/material";
import { styled as styledMui } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import farmimage from "../assets/agric.jpg";

import { motion } from "framer-motion";

// MUI Icons
import AgricultureIcon from "@mui/icons-material/Agriculture";
import ScienceIcon from "@mui/icons-material/Science";
import BugReportIcon from "@mui/icons-material/BugReport";
import SensorsIcon from "@mui/icons-material/Sensors";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ForumIcon from "@mui/icons-material/Forum";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Styled MUI AppBar
const Navbar = styledMui(AppBar)(({ theme }) => ({
  backgroundColor: "#2e7d32",
  color: "#fff",
  height: 80,
  justifyContent: "center",
  boxShadow: "none",
  borderBottom: "1px solid rgba(255,255,255,0.2)",
}));

// Styled Components
const ToolbarContainer = styled(Toolbar)`
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  padding: 0 30px;
`;

const LogoText = styled(Typography)`
  font-weight: bold;
  font-size: 1.8rem;
  cursor: pointer;
`;

const NavButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const NavButton = styled(Button)`
  font-size: 1.1rem;
`;

const HeroSection = styled.div`
  width: 100vw;
  height: 100vh;
  background: url(${farmimage}) no-repeat center center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 20px;
`;

const FeaturesSection = styled.div`
  width: 100%;
  padding: 80px 20px;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FeaturesHeading = styled(Typography)`
  font-size: 2.2rem;
  font-weight: bold;
  margin-bottom: 50px;
  text-align: center;
`;

const FeaturesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  width: 100%;
  max-width: 1100px;
`;

const FeatureCard = styled.div`
  width: 240px;
  background-color: #ffffff;
  border-radius: 16px;
  padding: 30px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: 0.3s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.15);
  }
`;

const FeatureIcon = styled.div`
  color: #2e7d32;
  margin-bottom: 15px;
`;

const FeatureTitle = styled(Typography)`
  font-size: 1.1rem;
  font-weight: 600;
`;

const FaqSection = styled.section`
  padding: 80px 20px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FaqHeading = styled(Typography)`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 40px;
  text-align: center;
  color: #2e7d32; /* Green color for the heading */
`;

const FaqItem = styled.div`
  max-width: 800px;
  width: 100%;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 10px;
  overflow: hidden;
`;

const FaqQuestion = styled.div`
  background-color: #f0f0f0;
  padding: 15px 20px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FaqAnswer = styled.div`
  padding: 15px 20px;
  background-color: #ffffff;
  font-size: 1rem;
  color: #333;
`;
const FooterContainer = styled.footer`
  background-color: #000;
  color: #fff;
  padding: 60px 20px 30px;
  text-align: center;
`;

const FooterLogo = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 10px;
  letter-spacing: 1px;
`;

const FooterTagline = styled.p`
  font-size: 1rem;
  color: #aaa;
  margin-bottom: 30px;
`;

const FooterNav = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 30px;
`;

const FooterLink = styled.button`
  background: none;
  border: none;
  color: #aaa;
  font-size: 0.95rem;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: #fff;
  }
`;

const FooterCopyright = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin-top: 20px;
`;


const HomePage = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("isLoggedIn")) {
      navigate("/");
    }
  }, [navigate]);

  const handleMenuClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    navigate("/");
  };

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const features = [
    {
      title: "Crop Recommendation",
      icon: <AgricultureIcon fontSize="large" />,
      route: "/crop",
    },
    {
      title: "Fertilizer Suggestion",
      icon: <ScienceIcon fontSize="large" />,
      route: "/fertilizer",
    },
    {
      title: "Pesticide Guidance",
      icon: <BugReportIcon fontSize="large" />,
      route: "/pesticide",
    },
    {
      title: "Weather-Based Advisory",
      icon: <SensorsIcon fontSize="large" />,
      route: "/weather",
    },
    {
      title: "Nitrogen Detection (LCC)",
      icon: <PrecisionManufacturingIcon fontSize="large" />,
      route: "/lcc",
    },
    {
      title: "Smart Notifications",
      icon: <ForumIcon fontSize="large" />,
      route: "/notification",
    },
  ];

  const faqs = [
    {
      question: "What is the purpose of this app?",
      answer:
        "This app is designed to help farmers reduce the use of pesticides and fertilizers by providing precise, data-driven recommendations. It integrates Soil Health Card data, weather information, and the Leaf Color Chart method to offer personalized advice on optimizing input usage and enhancing crop health, thereby promoting sustainable agricultural practices.",
    },
    {
      question: "How do I get accurate recommendations for crops and fertilizers?",
      answer:
        "Simply input soil data such as pH, nitrogen, phosphorus, potassium levels, and other environmental factors. The system will use this data to give tailored suggestions.",
    },
    {
      question: "How does the pest detection system work?",
      answer:
        "Users upload photos of affected crops, and the AI analyzes the images to detect common pests, suggesting suitable pesticide treatments.",
    },
    {
      question: "What is the Leaf Color Chart (LCC) method, and how does the app incorporate it?",
      answer:
        "The Leaf Color Chart (LCC) method is used to assess the nitrogen status of crops based on the color of the leaves. The app uses real-time LCC data to monitor your crops and provide precise fertilizer recommendations based on the nitrogen content needed at various growth stages, reducing unnecessary fertilizer usage.Yes! The platform is fully mobile-compatible, allowing farmers to access recommendations and tools on smartphones and tablets.",
    },
    {
      question: "Is this service available for all types of farms. Can this app help me reduce costs in my farming operations?",
      answer:
        "Yes, Smart Farm Assistant Yes, by optimizing the use of fertilizers and pesticides, the app helps you avoid unnecessary purchases and reduces input costs. This ensures that you’re only applying the right amount of resources when needed, leading to cost savings and more efficient farming practices.is designed for farms of all sizes and types, whether small-scale organic farms or large commercial operations.",
    },
  ];

  return (
    <>
      {/* Navbar */}
      <Navbar position="fixed">
        <ToolbarContainer>
          <LogoText variant="h6" onClick={() => navigate("/home")}>
            Growsphere
          </LogoText>

          <NavButtonsContainer>
            <NavButton color="inherit" onClick={() => navigate("/home")}>Home</NavButton>
            <NavButton color="inherit" onClick={() => navigate("/crop")}>Crop</NavButton>
            <NavButton color="inherit" onClick={() => navigate("/fertilizer")}>Fertilizer</NavButton>
            <NavButton color="inherit" onClick={() => navigate("/weather")}>Weather</NavButton>
            <NavButton color="inherit" onClick={() => navigate("/lcc")}>Leaf Colour Chart</NavButton>
          
            <IconButton color="inherit" onClick={handleMenuClick}>
              {/* Account Icon here if needed */}
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </NavButtonsContainer>
        </ToolbarContainer>
      </Navbar>

      {/* Hero Section */}
      <HeroSection>
        <Overlay />
        <HeroContent>
          <Typography variant="h2" gutterBottom>
            Growsphere — Smart Agriculture
          </Typography>
          <Typography variant="h5">
            A data-driven platform helping farmers reduce chemical use and improve sustainability.
          </Typography>
        </HeroContent>
      </HeroSection>

      {/* Features Section */}
      <FeaturesSection>
        <FeaturesHeading variant="h4">
          Empowering Farmers with <span style={{ color: '#2e7d32' }}>Smart Solutions</span>
        </FeaturesHeading>
        <FeaturesGrid>
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => navigate(feature.route)}
              style={{ cursor: "pointer" }}
            >
              <FeatureCard>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
              </FeatureCard>
            </motion.div>
          ))}
        </FeaturesGrid>
      </FeaturesSection>

      {/* FAQ Section */}
      <FaqSection>
        <FaqHeading>❓ Frequently Asked Questions</FaqHeading>
        {faqs.map((faq, index) => (
          <FaqItem key={index}>
            <FaqQuestion onClick={() => toggleFaq(index)}>
              {faq.question}
              {openIndex === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </FaqQuestion>
            {openIndex === index && (
              <FaqAnswer>{faq.answer}</FaqAnswer>
            )}
          </FaqItem>
        ))}
      </FaqSection>
      <FooterContainer>
  <FooterLogo>GrowSphere</FooterLogo>
  <FooterTagline>Empowering Modern Agriculture</FooterTagline>

  <FooterNav>
    <FooterLink onClick={() => navigate("/home")}>Home</FooterLink>
    <FooterLink onClick={() => navigate("/about")}>About</FooterLink>
    <FooterLink onClick={() => navigate("/services")}>Services</FooterLink>
    <FooterLink onClick={() => navigate("/contact")}>Contact</FooterLink>
  </FooterNav>

  <FooterCopyright>
    © {new Date().getFullYear()} GrowSphere. All rights reserved.
  </FooterCopyright>
</FooterContainer>

    </>
  );
};

export default HomePage;
